import {
  Accordion,
  AccordionContent,
  AccordionItem,
  IconButton,
  Icons,
  useToggle,
} from '@commenda/kakashi';
import { IChatRoomMessage } from '@commenda/types/src/chat-room/types';
import { FileBasic } from '@commenda/types/src/file/types';
import {
  DocumentViewUIHelper,
  downloadFileFromLink,
  getFileIcon,
  timeAgo,
} from '@commenda/utils';
import { useState } from 'react';
import { EmptyStateV2 } from '../../../components/empty-state/EmptyStateV2';
import { PreviewItemModal } from '../../../components/PreviewModal';
import { ChatProps } from '../../ChatRoom/types';

type ChatDocumentsViewProps = {
  messages: IChatRoomMessage[];
  onFileDownload: ChatProps['onFileDownload'];
  chatroom?: { id: string; displayName?: string };
};

const DocumentsView = ({
  messages,
  onFileDownload,
}: ChatDocumentsViewProps) => {
  const [previewFile, setPreviewFile] = useState<FileBasic | null>(null);
  const previewModalVisibility = useToggle(false);

  const uploadedDocuments = messages
    ?.filter((message) => message.files?.length > 0)
    ?.map((message) => message.files)
    .flat() ?? [];

  return (
    <div className="h-full flex flex-col">
      {uploadedDocuments.length === 0 && (
        <EmptyStateV2
          title="No documents shared"
          description="Any documents shared will appear here"
          className="bg-white rounded-none border-none"
        />
      )}

      <div className="flex flex-col flex-1 overflow-y-auto">
        {uploadedDocuments.length !== 0 && (
          <Accordion type="single" collapsible defaultValue="uploaded-document">
            <AccordionItem value="uploaded-document" key="uploaded-document">
              <AccordionContent className="bg-neutral-7">
                <div className="flex flex-col divide-y divide-border-primary">
                  {uploadedDocuments?.map((document) => {
                    const FileIconComponent: React.ElementType = getFileIcon(
                      document.fileName || ''
                    );
                    return (
                      <div
                        key={document.id}
                        className="flex flex-col divide-y divide-border-primary"
                      >
                        <div className="flex items-center justify-between gap-lg p-xl">
                          <div className="flex items-center gap-lg">
                            <FileIconComponent className="size-8" />
                            <div className="flex flex-col gap-xs">
                              <span className="txt-small-medium text-text-secondary">
                                {document.fileName}
                              </span>
                              <span className="txt-extra-small-regular text-text-quaternary">
                                {timeAgo(
                                  new Date(
                                    document.lastUpdateTime
                                  ).toISOString()
                                )}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-xs">
                            {DocumentViewUIHelper.shouldShowPreview(
                              document.fileName || ''
                            ) && (
                              <IconButton
                                variant="default"
                                size="sm"
                                icon={<Icons.Eye />}
                                onClick={() => {
                                  setPreviewFile(document);
                                  previewModalVisibility.show();
                                }}
                                aria-label="Preview file"
                              />
                            )}
                            <IconButton
                              variant="default"
                              size="sm"
                              icon={<Icons.Download01 />}
                              onClick={async () => {
                                const res = await onFileDownload(document.id);
                                downloadFileFromLink(
                                  res.signedUrl,
                                  document.fileName
                                );
                              }}
                              aria-label="Download file"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
      </div>

      <PreviewItemModal
        isOpen={previewModalVisibility.visible}
        onClose={previewModalVisibility.hide}
        previewFile={previewFile}
      />
    </div>
  );
};

export default DocumentsView;
